import type { IAppLayoutComponentName } from '@sixui/ui';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  AppLayout,
  generateThemeFromSourceColor,
  isValidHexColor,
  Stack,
  ThemeSetterContext,
} from '@sixui/ui';
import { appLayoutTokens } from '@sixui/ui/components/AppLayout/AppLayout.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import {
  APP_PREVIEW_TRAILING_SPACE,
  useAppPreviewState,
} from '~/components/organisms/AppPreview';
import { useDensity } from '~/components/organisms/fields/DensitySwitch';
import { Footer } from '~/components/organisms/Footer';
import { Header } from '~/components/organisms/Header';
import { NavigationRail } from '~/components/organisms/NavigationRail';
import { Sidebar } from '~/components/organisms/Sidebar';
import { TopLoadingBarWithState } from '~/components/organisms/TopLoadingBar';
import { PwaReloadPrompt } from '~/components/utils/PwaReloadPrompt';
import { SentryTags } from '~/components/utils/SentryTags';
import { ITimeUnit } from '~/helpers/types';
import { useScrollToTop } from '~/hooks/useScrollToTop';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { DashboardContext } from './DashboardContext';

export type IDashboardTemplateProps = {
  children: React.ReactNode;
  organizationId?: string;
  appId?: string;
  noAppPreviewOverlap?: boolean;
  hasAside?: boolean;
  wide?: boolean;
};

const styles = stylex.create({
  appLayout: {
    [appLayoutTokens.dividerColor]: colorSchemeTokens.outlineVariant,
  },
  container: {
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    paddingBottom: '1.5rem',
    width: '100%',
  },
  container$withAppPreview: (marginRight: number) => ({
    marginRight,
  }),
  content: {
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  content$md: {
    maxWidth: 1200,
  },
  content$wide: {
    maxWidth: 1600,
  },
});

export const DashboardTemplate: React.FC<IDashboardTemplateProps> = (props) => {
  const {
    children,
    organizationId: organizationIdProp,
    appId,
    noAppPreviewOverlap,
    wide,
    hasAside,
  } = props;

  useScrollToTop();

  const appQuery = useAppQuery(appId !== undefined ? { appId } : undefined);
  const themeSetterContext = useContext(ThemeSetterContext);
  const [density] = useDensity();
  const { t } = useTranslation();
  const app = appQuery.data;
  const appPrimaryColor = app?.settings?.press?.env?.appThemeColorPrimary;
  const appTheme = useMemo(() => {
    if (appPrimaryColor && isValidHexColor(appPrimaryColor)) {
      // TODO: cache theme
      return generateThemeFromSourceColor(appPrimaryColor);
    }

    return undefined;
  }, [appPrimaryColor]);
  const [asideElement, setAsideElement] = useState<HTMLDivElement | null>(null);
  const organizationId = appQuery.data?.organization?._id ?? organizationIdProp;

  useEffect(() => {
    themeSetterContext?.setTheme({
      ...appTheme,
      density: {
        density: String(density),
        interval: '4px',
        minTargetSize: density < 0 ? '100%' : 'max(100%, 48px)',
      },
    });
  }, [themeSetterContext, appTheme, density]);

  const appPreviewState = useAppPreviewState();
  const appPreviewHorizontalSpace =
    (appPreviewState.width ?? 0) + APP_PREVIEW_TRAILING_SPACE;
  const thumbUrl = app?.icon?.thumbUrl;

  return (
    <DashboardContext.Provider value={{ asideElement }}>
      <SentryTags />

      <Helmet
        titleTemplate={
          app
            ? t('seo.titleTemplate', {
                title: `%s - ${app?.name}`,
              })
            : undefined
        }
      >
        {thumbUrl ? (
          <link rel="icon" href={thumbUrl} sizes="150x150" />
        ) : (
          <link rel="icon" href="/static/icons/32x32.png" sizes="32x32" />
        )}
      </Helmet>
      <TopLoadingBarWithState
        showAfterMinLoadingTime={300 * ITimeUnit.MILLISECOND}
      />
      <PwaReloadPrompt />

      <AppLayout
        components={[
          'header',
          'navigationRail',
          'navigationDrawer',
          ...(hasAside ? ['aside' as IAppLayoutComponentName] : []),
        ]}
        preferredNavigationMode="standard"
        sx={styles.appLayout}
      >
        <Stack>
          <Stack horizontal align="start">
            <AppLayout.SideSheet fullHeight>
              <NavigationRail organizationId={organizationId} appId={appId} />
              <Sidebar organizationId={organizationId} appId={appId} />
            </AppLayout.SideSheet>

            <Stack align="stretch" grow>
              <Header />

              <Stack horizontal align="start">
                <AppLayout.Body horizontal>
                  <div
                    {...stylex.props(
                      styles.container,
                      appPreviewState.opened &&
                        noAppPreviewOverlap &&
                        styles.container$withAppPreview(
                          appPreviewHorizontalSpace,
                        ),
                    )}
                  >
                    <Stack
                      gap={6}
                      sx={[
                        styles.content,
                        wide ? styles.content$wide : styles.content$md,
                      ]}
                    >
                      {children}
                    </Stack>
                  </div>
                </AppLayout.Body>
              </Stack>
            </Stack>

            <AppLayout.SideSheet anchor="right" fullHeight>
              <div id="aside" ref={setAsideElement} />
            </AppLayout.SideSheet>
          </Stack>
        </Stack>

        <Footer />
      </AppLayout>
    </DashboardContext.Provider>
  );
};
